import { useKeyboardControls, Trail } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { RigidBody, useRapier } from '@react-three/rapier'
import { useEffect, useRef, useState, useMemo } from 'react'
import * as THREE from 'three'
import useGame from './stores/useGame'

export default function Player()
{
    const body = useRef()
    const ballRef = useRef()
    const [subscribeKeys, getKeys] = useKeyboardControls()
    const { rapier, world } = useRapier()

    const [smoothedCameraPosition] = useState(() => new THREE.Vector3(10, 10, 10))
    const [smoothedCameraTarget] = useState(() => new THREE.Vector3())

    const start = useGame((state) => state.start)
    const end = useGame((state) => state.end)
    const fail = useGame((state) => state.fail)
    const selectedBall = useGame((state) => state.selectedBall)

    const jump = () => {
        const currentPhase = useGame.getState().phase
        if (currentPhase !== 'playing' && currentPhase !== 'ready') return

        if (!body.current) return

        const origin = body.current.translation()
        origin.y -= 0.31
        const direction = { x: 0, y: -1, z: 0 }
        const ray = new rapier.Ray(origin, direction)
        const hit = world.castRay(ray, 10, true)

        if (hit && hit.timeOfImpact < 0.15) {
            body.current.applyImpulse({ x: 0, y: 0.7, z: 0 })
        }
    }

    const reset = () => {
        if (!body.current) return
        body.current.setTranslation({ x: 0, y: 1, z: 0 })
        body.current.setLinvel({ x: 0, y: 0, z: 0 })
        body.current.setAngvel({ x: 0, y: 0, z: 0 })
    }

    useEffect(() => {
        const unsubscribeReset = useGame.subscribe(
            (state) => state.phase,
            (value) => {
                if (value === 'ready') {
                    reset()
                }
            }
        )

        const unsubscribeJump = subscribeKeys(
            (state) => state.jump,
            (value) => {
                if (value) {
                    jump()
                }
            }
        )

        const unsubscribeAny = subscribeKeys(() => {
            const currentPhase = useGame.getState().phase
            if (currentPhase === 'ready') {
                start()
            }
        })

        return () => {
            unsubscribeReset()
            unsubscribeJump()
            unsubscribeAny()
        }
    }, [subscribeKeys, start])

    useFrame((state, delta) => {
        if (!body.current) return

        const bodyPosition = body.current.translation()
        const currentPhase = useGame.getState().phase

        // Controls - faqat playing holatda ishlaydi
        if (currentPhase === 'playing') {
            const { forward, backward, leftward, rightward } = getKeys()

            const impulse = { x: 0, y: 0, z: 0 }
            const torque = { x: 0, y: 0, z: 0 }

            const impulseStrength = 0.9 * delta
            const torqueStrength = 0.27 * delta

            if (forward) {
                impulse.z -= impulseStrength
                torque.x -= torqueStrength
            }

            if (rightward) {
                impulse.x += impulseStrength
                torque.z -= torqueStrength
            }

            if (backward) {
                impulse.z += impulseStrength
                torque.x += torqueStrength
            }

            if (leftward) {
                impulse.x -= impulseStrength
                torque.z += torqueStrength
            }

            body.current.applyImpulse(impulse)
            body.current.applyTorqueImpulse(torque)
        }

        // Camera - har doim ishlaydi
        const cameraPosition = new THREE.Vector3()
        cameraPosition.copy(bodyPosition)
        cameraPosition.z += 2.25
        cameraPosition.y += 0.65

        const cameraTarget = new THREE.Vector3()
        cameraTarget.copy(bodyPosition)
        cameraTarget.y += 0.25

        smoothedCameraPosition.lerp(cameraPosition, 5 * delta)
        smoothedCameraTarget.lerp(cameraTarget, 5 * delta)

        state.camera.position.copy(smoothedCameraPosition)
        state.camera.lookAt(smoothedCameraTarget)

        // Win/Lose conditions - faqat playing da tekshiriladi
        if (currentPhase === 'playing') {
            const currentBlocksCount = useGame.getState().blocksCount

            // Win - finish platformasiga yetdi
            // Finish platforma markazi z = -(blocksCount + 1) * 4 da
            // Platforma 4 unit uzunlikda, shuning uchun old tomoni = markaz + 2
            // Ball finish platformasining old qismiga yetganda g'alaba
            const finishZ = -(currentBlocksCount + 1) * 4 + 1.5
            if (bodyPosition.z < finishZ) {
                end()
            }

            // Lose - tushib ketdi
            if (bodyPosition.y < -4) {
                fail()
            }
        }
    })

    return (
        <RigidBody
            ref={body}
            canSleep={false}
            colliders="ball"
            restitution={0.2}
            friction={1}
            linearDamping={0.5}
            angularDamping={0.5}
            position={[0, 1, 0]}
        >
            {/* Trail effect - ball harakatini ko'rsatadi */}
            <Trail
                width={0.8}
                length={6}
                color={selectedBall.emissive}
                attenuation={(t) => t * t}
                decay={1.5}
            >
                <group ref={ballRef}>
                    {/* Main ball - optimized glossy look */}
                    <mesh castShadow>
                        <sphereGeometry args={[0.3, 32, 32]} />
                        <meshStandardMaterial
                            color={selectedBall.color}
                            emissive={selectedBall.emissive}
                            emissiveIntensity={0.6}
                            metalness={0.3}
                            roughness={0.15}
                        />
                    </mesh>
                    {/* Inner glow - depth effect */}
                    <mesh>
                        <sphereGeometry args={[0.24, 24, 24]} />
                        <meshBasicMaterial
                            color={selectedBall.emissive}
                            transparent
                            opacity={0.35}
                        />
                    </mesh>
                </group>
            </Trail>
        </RigidBody>
    )
}
